/**
 * Tunnel world geometry (FS-03). The playfield is the disc of radius PLAY_RADIUS about the Z axis;
 * hazards and the ship live in tunnel-polar coordinates.
 *
 * Convention (FS-06 §4): θ = 0 at +Y, increasing toward +X, so θ = wrap2pi(atan2(x, y));
 * r = hypot(x, y).
 */
import { PLAY_RADIUS } from '../core/constants';
import { dsqrt, wrap2pi } from '../core/math-det';

const WALL_BAND = 2.0; // u. Soft band near the playfield edge (FS-05 §4.3).

export interface Vec2Out {
  x: number;
  y: number;
}

export class TunnelWorld {
  readonly playRadius = PLAY_RADIUS;

  toTheta(x: number, y: number): number {
    return wrap2pi(Math.atan2(x, y));
  }

  toRadius(x: number, y: number): number {
    return dsqrt(x * x + y * y);
  }

  /** Clamp a position into the playfield disc. Writes into `out`. Velocity is unaffected. */
  clampToPlayfield(x: number, y: number, out: Vec2Out): void {
    const r = dsqrt(x * x + y * y);
    if (r <= PLAY_RADIUS || r === 0) {
      out.x = x;
      out.y = y;
      return;
    }
    const k = PLAY_RADIUS / r;
    out.x = x * k;
    out.y = y * k;
  }

  /** Penetration depth into the soft wall band, 0 outside it. */
  wallPenetration(x: number, y: number): number {
    const r = dsqrt(x * x + y * y);
    const edge = PLAY_RADIUS - WALL_BAND;
    return r > edge ? r - edge : 0;
  }

  /** Outward unit normal at (x, y). Writes into `out`. */
  outwardNormal(x: number, y: number, out: Vec2Out): void {
    const r = dsqrt(x * x + y * y);
    if (r === 0) {
      out.x = 0;
      out.y = 1;
      return;
    }
    out.x = x / r;
    out.y = y / r;
  }
}
